import { useState, useEffect, useCallback } from 'react';
import * as portalApi from '../../services/portal/portalApi';

/**
 * Hook principal del portal del cliente
 */
export const usePortalDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await portalApi.getPortalDashboard();
      setDashboard(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { dashboard, loading, error, refetch: fetchDashboard };
};

// Filtra productos bloqueados/inactivos del portal (agotados sí se muestran)
const filterAvailableProducts = (data) => {
  const hiddenStatuses = ['BLOCKED', 'INACTIVE', 'DISCONTINUED'];
  return (data || []).filter(p => {
    if (p.status && hiddenStatuses.includes(p.status)) return false;
    return true;
  });
};

/**
 * Hook para productos nuevos
 */
export const useNewProducts = (limit = 20) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNewProducts = async () => {
      try {
        setLoading(true);
        const data = await portalApi.getNewProducts({ limit });
        setProducts(filterAvailableProducts(data));
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNewProducts();
  }, [limit]);

  return { products, loading, error };
};

/**
 * Hook para productos públicos (sin autenticación)
 * Para mostrar en el portal antes de hacer login
 */
export const usePublicProducts = (limit = 10) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublicProducts = async () => {
      try {
        setLoading(true);
        const data = await portalApi.getPublicProducts({ limit });
        setProducts(filterAvailableProducts(data));
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicProducts();
  }, [limit]);

  return { products, loading, error };
};

/**
 * Hook para productos en oferta
 */
export const useProductsOnSale = (limit = 20) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSaleProducts = async () => {
      try {
        setLoading(true);
        const data = await portalApi.getProductsOnSale({ limit });
        setProducts(filterAvailableProducts(data));
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSaleProducts();
  }, [limit]);

  return { products, loading, error };
};

/**
 * Hook para productos frecuentemente comprados
 */
export const useFrequentlyPurchased = (limit = 10) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFrequentProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await portalApi.getFrequentlyPurchased({ limit });
      setProducts(filterAvailableProducts(data));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchFrequentProducts();
  }, [fetchFrequentProducts]);

  return { products, loading, error, refetch: fetchFrequentProducts };
};

/**
 * Hook para órdenes del portal
 */
export const usePortalOrders = (params = {}) => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serializar params para evitar loop infinito
  const paramsString = JSON.stringify(params);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const parsedParams = JSON.parse(paramsString);
      const data = await portalApi.getPortalOrders(parsedParams);
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [paramsString]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { orders, total, loading, error, refetch: fetchOrders };
};

/**
 * Hook para facturas del portal
 */
export const usePortalInvoices = (params = {}) => {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serializar params para evitar loop infinito
  const paramsString = JSON.stringify(params);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const parsedParams = JSON.parse(paramsString);
      const data = await portalApi.getPortalInvoices(parsedParams);
      setInvoices(data.invoices || []);
      setSummary(data.summary || null);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [paramsString]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return { invoices, summary, total, loading, error, refetch: fetchInvoices };
};

/**
 * Hook para perfil del cliente
 */
export const usePortalProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const data = await portalApi.getPortalProfile();
      setProfile(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProfile = async (profileData) => {
    try {
      const data = await portalApi.updatePortalProfile(profileData);
      setProfile(data);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, loading, error, updateProfile, refetch: fetchProfile };
};
